import { useMemo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoadOrderHistory, useLoadRevenueHistory, useLoadCostPrice, useLoadMenus, useLoadSubrecipes } from '../../../utils/dataLoadingUtils';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { filterOrdersByDateRange } from '../../../utils/dateRangeFilters';
import { filterRevenueByDateRange, getCombinedDateRange } from '../../../utils/dateRangeFilters';
import { TopSection } from '../../ui/TopSection';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { saveVatOrdersSetting, saveVatSetting } from '../../../stores/appUserDefaultStore';
import {
  calculateCategorySpentBreakdown,
  calculateMenuEngineeringMatrix,
  getMenuEngineeringTimeWindow,
  calculateRevenueVsInslag,
  calculateCostPercentageOverTime,
  calculateSpendingSplit,
  calculateProductCostPercentageOverTime,
  calculateSalesPerProduct,
  calculateTimePeriods,
  calculateMenuCostVsActual,
  buildPeriodContexts,
} from '../../../utils/chartsUtils';
import { enrichCostPriceListWithCurrentPrices } from '../../../utils/costPriceUtils';
import { useByRestaurantCostData } from '../../../hooks/useByRestaurantCostData';
import { filterOrdersByOverallType } from '../../orderhistory/utils';
import { filterRevenueByOverallType } from '../../revenuehistory/utils';
import { RevenueSourceType } from '../../../types/RevenueSourceType';
import { CategorySpentChart } from './CategorySpentChart';
import { MenuEngineeringChart } from './MenuEngineeringChart';
import { RevenueVsInslagChart } from './RevenueVsInslagChart';
import { InslagOverTimeChart } from './InslagOverTimeChart';
import { SpendingSplitChart } from './SpendingSplitChart';
import { ProductCostOverTimeChart } from './ProductCostOverTimeChart';
import { SalesPerProductChart } from './SalesPerProductChart';
import { MenuCostVsActualChart } from './MenuCostVsActualChart';

// Stable "load everything" floor for the progressive loaders.
const BEGINNING_OF_TIME = new Date(0);
// Mirrors the floor useTimeControls uses when it derives the 'all' date range.
const ALL_RANGE_MIN_DATE = new Date('2020-01-01');

const ChartsPage = () => {
  const { t } = useTranslation();

  // Load data. Order history + revenue are loaded progressively further down, once we know the
  // date window the charts actually need (see requiredFrom).
  useLoadCostPrice();
  useLoadMenus();
  useLoadSubrecipes();

  // Connect to stores
  const orderHistoryStore = useOrderHistory();
  const revenueHistoryStore = useRevenueHistory();
  const costPriceStore = useCostPriceStore();
  const subrecipesById = useSubrecipesStore(s => s.subrecipesById);

  // Every cost-price-driven chart needs orders + revenue + cost price + custom ingredients +
  // subrecipes TOGETHER: a dish's cost comes from the orders, its menu price from the revenue.
  // Without this gate the whole memo chain below re-ran on every dataset arrival — most expensively
  // on the one where revenue had landed but the order list was still empty, producing a result that
  // was thrown away seconds later. Subscribe to the raw "loaded for this restaurant" state (not the
  // store's isDataLoadedFor method, which wouldn't re-render this component when the data lands).
  const ordersFullyLoaded = useOrderHistory(s => s.fullyLoaded);
  const revenueFullyLoaded = useRevenueHistory(s => s.fullyLoaded);
  const costPriceRestaurantId = useCostPriceStore(s => s.currentRestaurantId);
  const customIngredientsRestaurantId = useCostPriceStore(s => s.customIngredientsRestaurantId);
  const subrecipesRestaurantId = useSubrecipesStore(s => s.currentRestaurantId);
  // A failed fetch never sets its restaurant id, so without these the charts would spin forever on an
  // error. "Settled" = loaded OR failed; on failure we compute with what we have, as before the gate.
  const costPriceError = useCostPriceStore(s => s.error);
  const customIngredientsError = useCostPriceStore(s => s.customIngredientsError);
  const subrecipesError = useSubrecipesStore(s => s.error);
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
  const showExVatRevenue = useAppUserDefaultStore(s => s.showExVat);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const isAllRestaurants = !!(selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant));
  const restaurantId = selectedRestaurant?.id;

  // "All Restaurants" never loads cost price / custom ingredients / subrecipes (see useLoadCostPrice
  // and useLoadSubrecipes), so requiring them there would leave the charts loading forever — that mode
  // has always rendered with empty cost data.
  const chartDataReady =
    ordersFullyLoaded &&
    revenueFullyLoaded &&
    (isAllRestaurants || (
      !!restaurantId &&
      (costPriceRestaurantId === restaurantId || !!costPriceError) &&
      (customIngredientsRestaurantId === restaurantId || !!customIngredientsError) &&
      (subrecipesRestaurantId === restaurantId || !!subrecipesError)
    ));

  // Land on the page BEFORE doing the heavy maths. Arriving from a page with warm stores (Dashboard),
  // chartDataReady is already true on the very first render, so the whole memo chain used to run
  // during that render — React couldn't commit and the sidebar click appeared to freeze. Gating the
  // heavy memos on a flag flipped from a macrotask lets the cheap shell (charts in their existing
  // loading state) commit and paint first, then the computation runs on the next render.
  // Deliberately NOT useDeferredValue: on React 18 it returns the value as-is on the initial render
  // (no initialValue param), which is precisely the render that freezes.
  const [hasPainted, setHasPainted] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setHasPainted(true), 0);
    return () => window.clearTimeout(id);
  }, []);
  const readyToCompute = chartDataReady && hasPainted;

  // Overall type filter
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    const availableOptions = ['all', 'food', 'drinks'];
    if (overallTypeFilter && !availableOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, setOverallTypeFilter]);

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks');
  };

  // Time controls
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Calculate time periods (12 data points)
  const timePeriods = useMemo(() =>
    calculateTimePeriods(endDate, selectedTimeOption as 'day' | 'week' | 'month' | 'quarter' | 'all', 12),
    [endDate, selectedTimeOption]
  );

  // ===== HOW FAR BACK THE CHARTS ACTUALLY READ =====
  // NOT startDate: the over-time charts build 12 historical periods back from endDate and read all
  // data up to endDate, so the true floor is the start of the EARLIEST of those periods (or startDate
  // when the user has dragged the range even further back).
  //
  // 'all' is special and MUST have the complete history: useTimeControls derives the 'all' range from
  // getCombinedDateRange() over the LOADED lists, so a partial load would make 'all' mean "all of what
  // happens to be loaded".
  const requiredFrom = useMemo(() => {
    if (selectedTimeOption === 'all') return BEGINNING_OF_TIME;
    const earliestPeriodStart = timePeriods[0]?.startDate;
    if (!earliestPeriodStart) return startDate;
    return earliestPeriodStart < startDate ? earliestPeriodStart : startDate;
  }, [selectedTimeOption, timePeriods, startDate]);

  // Progressive load: the first request covers requiredFrom -> now so the window charts paint fast;
  // the rest of the history streams in behind it. Changing the period (day -> quarter, paging back)
  // moves requiredFrom older, and the hooks call ensureViewLoaded() so the gap gets fetched.
  useLoadOrderHistory({ progressive: true, viewStart: requiredFrom });
  useLoadRevenueHistory({ progressive: true, viewStart: requiredFrom });

  // Coverage, not completeness: a chart whose inputs live entirely inside [requiredFrom, endDate] is
  // correct as soon as that window is on the client — it does not have to wait for the full history.
  const requiredFromTime = requiredFrom.getTime();
  const ordersWindowLoaded = useOrderHistory(s =>
    s.fullyLoaded || (s.loadedFromDate !== null && s.loadedFromDate.getTime() <= requiredFromTime));
  const revenueWindowLoaded = useRevenueHistory(s =>
    s.fullyLoaded || (s.loadedFromDate !== null && s.loadedFromDate.getTime() <= requiredFromTime));

  // The cost charts DO need the whole history (see chartDataReady), so once the window has landed ask
  // for everything else in ONE request (the pump fetches the gap down to desiredFloor in a single go)
  // instead of letting it crawl back month by month.
  const ordersFirstChunkLoaded = useOrderHistory(s => s.firstChunkLoaded);
  const revenueFirstChunkLoaded = useRevenueHistory(s => s.firstChunkLoaded);
  const ensureOrdersLoaded = useOrderHistory(s => s.ensureViewLoaded);
  const ensureRevenueLoaded = useRevenueHistory(s => s.ensureViewLoaded);
  useEffect(() => {
    if (ordersFirstChunkLoaded && !ordersFullyLoaded) ensureOrdersLoaded(BEGINNING_OF_TIME);
  }, [ordersFirstChunkLoaded, ordersFullyLoaded, ensureOrdersLoaded]);
  useEffect(() => {
    if (revenueFirstChunkLoaded && !revenueFullyLoaded) ensureRevenueLoaded(BEGINNING_OF_TIME);
  }, [revenueFirstChunkLoaded, revenueFullyLoaded, ensureRevenueLoaded]);

  // Switching INTO 'all' while history is still streaming would have useTimeControls compute the range
  // from the partially-loaded lists and never revisit it (its own healing effect only fires while the
  // range is still the untouched default). Recompute once the full history is in, so 'all' spans the
  // real data range — matching the pre-progressive behaviour exactly.
  const healedAllRangeRef = useRef(false);
  useEffect(() => {
    if (selectedTimeOption !== 'all') {
      healedAllRangeRef.current = false;
      return;
    }
    if (healedAllRangeRef.current || !ordersFullyLoaded || !revenueFullyLoaded) return;
    healedAllRangeRef.current = true;

    const fullRange = getCombinedDateRange(orderHistoryStore.orderHistoryList, revenueHistoryStore.revenueHistoryList);
    if (!fullRange) return;
    // Same floor useTimeControls applies when it derives the 'all' range.
    const cappedStart = fullRange.start < ALL_RANGE_MIN_DATE ? ALL_RANGE_MIN_DATE : fullRange.start;
    if (cappedStart < startDate) {
      setDateRange({ start: cappedStart, end: endDate });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeOption, ordersFullyLoaded, revenueFullyLoaded]);

  // Handler for Revenue VAT toggle
  const handleRevenueVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      await saveVatSetting(showExVat);
    } catch (error) {
      console.error('Failed to save Revenue VAT setting:', error);
    }
  };

  // Handler for Orders VAT toggle
  const handleOrdersVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      await saveVatOrdersSetting(showExVat);
    } catch (error) {
      console.error('Failed to save Orders VAT setting:', error);
    }
  };

  // ===== COMMON DATA PREPARATION =====
  // For time-based charts: Use ALL data up to endDate (not filtered by startDate)
  // This allows charts to show historical trends (12 periods back)
  const allDataUpToEndDateOrders = useMemo(() => {
    // Get earliest date from orders or use a very early date
    const earliestDate = orderHistoryStore.orderHistoryList.length > 0
      ? new Date(Math.min(...orderHistoryStore.orderHistoryList.map(o => new Date(o.orderDate).getTime())))
      : new Date(0);
    return filterOrdersByDateRange(orderHistoryStore.orderHistoryList, earliestDate, endDate);
  }, [orderHistoryStore.orderHistoryList, endDate]);

  const allDataUpToEndDateRevenue = useMemo(() => {
    // Get earliest date from revenue or use a very early date
    const earliestDate = revenueHistoryStore.revenueHistoryList.length > 0
      ? new Date(Math.min(...revenueHistoryStore.revenueHistoryList.map(r => new Date(r.date).getTime())))
      : new Date(0);
    return filterRevenueByDateRange(revenueHistoryStore.revenueHistoryList, earliestDate, endDate);
  }, [revenueHistoryStore.revenueHistoryList, endDate]);

  // For current period charts: Use date-filtered data (startDate to endDate)
  const dateFilteredOrders = useMemo(() =>
    filterOrdersByDateRange(orderHistoryStore.orderHistoryList, startDate, endDate),
    [orderHistoryStore.orderHistoryList, startDate, endDate]
  );

  const dateFilteredRevenue = useMemo(() =>
    filterRevenueByDateRange(revenueHistoryStore.revenueHistoryList, startDate, endDate),
    [revenueHistoryStore.revenueHistoryList, startDate, endDate]
  );

  // Filter by overall type - for time-based charts (all data up to endDate)
  const filteredOrdersForTimeCharts = useMemo(() =>
    filterOrdersByOverallType(allDataUpToEndDateOrders, overallTypeFilter),
    [allDataUpToEndDateOrders, overallTypeFilter]
  );

  const filteredRevenueForTimeCharts = useMemo(() =>
    filterRevenueByOverallType(allDataUpToEndDateRevenue, overallTypeFilter),
    [allDataUpToEndDateRevenue, overallTypeFilter]
  );

  // Filter by overall type - for current period charts (date-filtered)
  const filteredOrders = useMemo(() =>
    filterOrdersByOverallType(dateFilteredOrders, overallTypeFilter),
    [dateFilteredOrders, overallTypeFilter]
  );

  const filteredRevenue = useMemo(() =>
    filterRevenueByOverallType(dateFilteredRevenue, overallTypeFilter),
    [dateFilteredRevenue, overallTypeFilter]
  );

  // Build revenue item type map (for filtering cost price analyses)
  const revenueItemTypeMap = useMemo(() => {
    const map = new Map<string, RevenueSourceType>();
    revenueHistoryStore.revenueHistoryList.forEach(revenue => {
      if (revenue.revenueData?.categories) {
        revenue.revenueData.categories.forEach(category => {
          category.items.forEach(item => {
            if (item.name && !map.has(item.name) && revenue.revenueSourceType) {
              map.set(item.name, revenue.revenueSourceType as RevenueSourceType);
            }
          });
        });
      }
    });
    return map;
  }, [revenueHistoryStore.revenueHistoryList]);

  // Cross-restaurant sourcing: load the sibling restaurants referenced by the
  // loaded analyses so borrowed ingredients price from their source restaurant
  // (otherwise they resolve against the current restaurant's orders → €0 cost).
  const byRestaurant = useByRestaurantCostData(costPriceStore.costPriceList, restaurantId);

  // Enrich cost price data with current prices FIRST (before filtering).
  // Use filteredOrdersForTimeCharts to only use data up to the selected period.
  // Pass revenueHistory so BYO dishes carry their weighted breakdown into the charts.
  const enrichedCostPriceList = useMemo(() => {
    if (!readyToCompute) return [];
    return enrichCostPriceListWithCurrentPrices(
      costPriceStore.costPriceList,
      filteredOrdersForTimeCharts,
      {
        customIngredients: costPriceStore.customIngredients,
        showExVat: showExVatOrders,
        allSubrecipes: subrecipesById,
        revenueHistory: revenueHistoryStore.revenueHistoryList,
        byRestaurant,
        currentRestaurantId: restaurantId,
      }
    );
  }, [readyToCompute, costPriceStore.costPriceList, filteredOrdersForTimeCharts, costPriceStore.customIngredients, showExVatOrders, subrecipesById, revenueHistoryStore.revenueHistoryList, byRestaurant, restaurantId]);

  // THEN filter enriched cost price analyses by overall type
  const filteredEnrichedCostPriceList = useMemo(() => {
    if (overallTypeFilter === 'all') {
      return enrichedCostPriceList;
    }

    const targetType = overallTypeFilter === 'food' ? RevenueSourceType.FOOD : RevenueSourceType.DRINKS;

    return enrichedCostPriceList.filter(analysis => {
      // Custom items without menuItemName - include in "all", exclude from food/drinks
      if (!analysis.menuItemName) {
        return false;
      }

      const itemType = revenueItemTypeMap.get(analysis.menuItemName);
      return itemType === targetType;
    });
  }, [enrichedCostPriceList, revenueItemTypeMap, overallTypeFilter]);

  // ===== CHART DATA CALCULATIONS =====
  // Menu Engineering Matrix - uses adaptive time window based on available revenue data
  const menuEngineeringData = useMemo(() => {
    const { window: timeWindow, cutoffDate } = getMenuEngineeringTimeWindow(filteredRevenueForTimeCharts, endDate);

    const data = calculateMenuEngineeringMatrix(
      filteredEnrichedCostPriceList,
      filteredRevenueForTimeCharts,
      showExVatRevenue,
      endDate,
      cutoffDate || undefined
    );

    return { data, hasEnoughData: timeWindow !== null, timeWindow };
  }, [filteredEnrichedCostPriceList, filteredRevenueForTimeCharts, showExVatRevenue, endDate]);

  // Revenue vs Inslag - uses all data up to endDate for historical periods
  const revenueVsInslagData = useMemo(() =>
    calculateRevenueVsInslag(
      filteredOrdersForTimeCharts,
      filteredRevenueForTimeCharts,
      timePeriods,
      overallTypeFilter as 'all' | 'food' | 'drinks',
      showExVatOrders,
      showExVatRevenue
    ),
    [filteredOrdersForTimeCharts, filteredRevenueForTimeCharts, timePeriods, overallTypeFilter, showExVatOrders, showExVatRevenue]
  );

  // Category Spend Breakdown
  const categorySpentData = useMemo(() =>
    calculateCategorySpentBreakdown(filteredOrders, showExVatOrders, t),
    [filteredOrders, showExVatOrders, t]
  );

  // Cost Percentage Over Time - uses all data up to endDate for historical periods
  const inslagOverTimeData = useMemo(() =>
    calculateCostPercentageOverTime(
      filteredOrdersForTimeCharts,
      filteredRevenueForTimeCharts,
      timePeriods,
      overallTypeFilter as 'all' | 'food' | 'drinks',
      showExVatOrders,
      showExVatRevenue
    ),
    [filteredOrdersForTimeCharts, filteredRevenueForTimeCharts, timePeriods, overallTypeFilter, showExVatOrders, showExVatRevenue]
  );

  // Spending Split
  const spendingSplitData = useMemo(() =>
    calculateSpendingSplit(filteredOrders, overallTypeFilter as 'all' | 'food' | 'drinks', showExVatOrders),
    [filteredOrders, overallTypeFilter, showExVatOrders]
  );

  // Product Cost % Over Time - uses all data up to endDate for historical periods
  // Pass NON-ENRICHED cost price list - enrichment happens per period for accurate historical tracking
  // First, filter the non-enriched cost price list by overall type
  const filteredCostPriceList = useMemo(() => {
    if (overallTypeFilter === 'all') {
      return costPriceStore.costPriceList;
    }

    const targetType = overallTypeFilter === 'food' ? RevenueSourceType.FOOD : RevenueSourceType.DRINKS;

    return costPriceStore.costPriceList.filter(analysis => {
      // Custom items without menuItemName - include in "all", exclude from food/drinks
      if (!analysis.menuItemName) {
        return false;
      }

      const itemType = revenueItemTypeMap.get(analysis.menuItemName);
      return itemType === targetType;
    });
  }, [costPriceStore.costPriceList, revenueItemTypeMap, overallTypeFilter]);

  // Shared per-period enrichment used by both over-time charts.
  // Uses UNFILTERED inputs so the result is stable across overall-type toggles.
  const periodContexts = useMemo(() => {
    if (!readyToCompute) return [];
    return buildPeriodContexts(
      costPriceStore.costPriceList,
      allDataUpToEndDateOrders,
      allDataUpToEndDateRevenue,
      timePeriods,
      showExVatOrders,
      costPriceStore.customIngredients,
      byRestaurant,
      restaurantId,
      subrecipesById
    );
  }, [readyToCompute, costPriceStore.costPriceList, allDataUpToEndDateOrders, allDataUpToEndDateRevenue, timePeriods, showExVatOrders, costPriceStore.customIngredients, byRestaurant, restaurantId, subrecipesById]);

  const productCostOverTimeData = useMemo(() =>
    calculateProductCostPercentageOverTime(
      filteredCostPriceList,
      periodContexts,
      allDataUpToEndDateRevenue,
      showExVatRevenue
    ),
    [filteredCostPriceList, periodContexts, allDataUpToEndDateRevenue, showExVatRevenue]
  );

  // Sales per Product
  const salesPerProductData = useMemo(() =>
    calculateSalesPerProduct(filteredRevenue, showExVatRevenue),
    [filteredRevenue, showExVatRevenue]
  );

  // Menu Cost vs Actual - uses per-period quantities for weighted, per-period spent/revenue for actual
  const menuCostVsActualData = useMemo(() =>
    calculateMenuCostVsActual(
      filteredCostPriceList,
      filteredOrdersForTimeCharts,
      filteredRevenueForTimeCharts,
      periodContexts,
      showExVatOrders,
      showExVatRevenue
    ),
    [filteredCostPriceList, filteredOrdersForTimeCharts, filteredRevenueForTimeCharts, periodContexts, showExVatOrders, showExVatRevenue]
  );

  return (
    <div>
      <MobilePageHeader title={t('charts.title')} right={<RestaurantSelector />} />

      <TopSection
        title={t('charts.title')}
        description={t('charts.description')}
        backButton={false}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        overallTypeOptions={['all', 'food', 'drinks']}
        onOverallTypeChange={handleOverallTypeChange}
        currentOverallTypeValue={overallTypeFilter as 'all' | 'food' | 'drinks'}
        onRevenueVatChange={handleRevenueVatChange}
        currentRevenueVatValue={showExVatRevenue ? 'excl' : 'incl'}
        onOrdersVatChange={handleOrdersVatChange}
        currentOrdersVatValue={showExVatOrders ? 'excl' : 'incl'}
      />

      <div className="mt-6 space-y-6">
        {/* Row 1: Menu Engineering Matrix (full width) */}
        <MenuEngineeringChart
          data={menuEngineeringData.data}
          hasEnoughData={menuEngineeringData.hasEnoughData}
          timeWindow={menuEngineeringData.timeWindow}
          loading={!readyToCompute}
        />

        {/* Row 2: Revenue vs Inslag | Category Spend Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RevenueVsInslagChart data={revenueVsInslagData} loading={!ordersWindowLoaded || !revenueWindowLoaded} />
          <CategorySpentChart data={categorySpentData} loading={!ordersWindowLoaded} />
        </div>

        {/* Row 3: Menu Cost vs Actual Cost (full width) */}
        <MenuCostVsActualChart
          data={menuCostVsActualData}
          isAllRestaurants={isAllRestaurants}
          overallTypeFilter={overallTypeFilter as 'all' | 'food' | 'drinks'}
          selectedTimeOption={selectedTimeOption as 'day' | 'week' | 'month' | 'quarter' | 'all'}
          loading={!readyToCompute}
        />

        {/* Row 4: Cost % Over Time | Spending Split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <InslagOverTimeChart data={inslagOverTimeData} loading={!ordersWindowLoaded || !revenueWindowLoaded} />
          <SpendingSplitChart data={spendingSplitData} loading={!ordersWindowLoaded} />
        </div>

        {/* Row 5: Product Cost % Over Time (full width) */}
        <ProductCostOverTimeChart data={productCostOverTimeData} loading={!readyToCompute} />

        {/* Row 6: Sales per Product (full width) */}
        <SalesPerProductChart data={salesPerProductData} loading={!revenueWindowLoaded} />
      </div>
    </div>
  );
};

export default ChartsPage;
